import moment from 'moment';
import * as Enums from '@/utils/enums';
import { MANAGER_PHOTOS, ONBOARDING_CUTOFF_DATE, OnboardingManager } from '@/PageComponents/Onboarding/onboarding-config';
import { AccountManager, ManagerMeeting } from '@/interfaces/api/models';

export type StepId = 'admin' | 'brand' | 'firstJob';
export const STEP_ORDER: StepId[] = ['admin', 'brand', 'firstJob'];

export interface ActiveIntegration {
    Partner: number;
    Status: number;
}

/** `null` means "unknown" (loading failed) and is always treated as incomplete. */
export interface OnboardingInputs {
    /** Employees who count towards the team: see countTeamMembers. */
    employeeCount: number | null;
    hasLogo: boolean | null;
    /** "No logo yet" on the logo: completes step 2 like an upload would (uploading stays available). */
    logoSkipped: boolean;
    integration: ActiveIntegration | null;
    completedImportCount: number | null;
    jobCount: number | null;
}

export interface StepsResult {
    complete: Record<StepId, boolean>;
    completedCount: number;
    allComplete: boolean;
    firstIncomplete: StepId | null;
}

export const isIntegrationConnected = (integration: ActiveIntegration | null): boolean =>
    !!integration && integration.Status === Enums.IntegrationStatus.Live;

/**
 * Employees from GET /Employee/GetEmployees who count towards step 1: active ones and live invites.
 * A revoked invite has had its login turned off, so it doesn't.
 */
export const countTeamMembers = (employees: any[]): number =>
    employees.filter(e => String(e?.InviteStatus ?? '').toLowerCase() !== 'revoked').length;

export const computeSteps = (inputs: OnboardingInputs): StepsResult => {
    const complete: Record<StepId, boolean> = {
        // An invite creates the employee straight away (pending until accepted), so it counts here too.
        admin: (inputs.employeeCount ?? 0) > 1,
        // Importing data and connecting accounting are offered, not required: not every business has either.
        brand: inputs.hasLogo === true || inputs.logoSkipped,
        firstJob: (inputs.jobCount ?? 0) > 0,
    };
    const completedCount = STEP_ORDER.filter(step => complete[step]).length;
    return {
        complete,
        completedCount,
        allComplete: completedCount === STEP_ORDER.length,
        firstIncomplete: STEP_ORDER.find(step => !complete[step]) ?? null,
    };
};

export const isOnboardingEligible = (subscriptionInfo: any, cutoff: string = ONBOARDING_CUTOFF_DATE): boolean => {
    const firstTrialDate = subscriptionInfo?.FirstTrialDate;
    if (!firstTrialDate) return false;
    const first = moment(firstTrialDate, moment.ISO_8601);
    return first.isValid() && first.isSameOrAfter(moment(cutoff, 'YYYY-MM-DD').startOf('day'));
};

const TRIAL_LICENSE = Enums.getEnumStringValue(Enums.LicenseType, Enums.LicenseType.Trial);

/**
 * The company has subscribed: Live access (or Live and owing) on a licence that is no longer the trial.
 * Test systems get Live access while still on a trial licence, and keep onboarding.
 */
export const hasSubscribed = (subscriptionInfo: any): boolean => {
    const accessStatus = subscriptionInfo?.AccessStatus;
    const live = accessStatus === Enums.AccessStatus.Live || accessStatus === Enums.AccessStatus.LiveAndOwing;
    return live && subscriptionInfo?.LicenseType !== TRIAL_LICENSE;
};

/** Jobs that move a company on to the normal dashboard: with a demo behind them, or without one. */
export const GRADUATE_JOBS_AFTER_DEMO = 3;
export const GRADUATE_JOBS_WITHOUT_DEMO = 6;

export interface GraduationProgress {
    allComplete: boolean;
    demoHeld: boolean;
    /** All jobs ever created (closed and archived too); null when unknown, which counts as none. */
    jobCount: number | null;
}

export type GraduationReason = 'subscribed' | 'all_steps_and_demo_held' | 'jobs_after_demo' | 'jobs_without_demo';

/**
 * Why the company has outgrown the onboarding dashboard, or null while it hasn't: it subscribed, or
 * finished every step and had its demo, or created enough jobs (fewer once the demo has been held).
 * The first matching reason wins, in that order.
 */
export const getGraduationReason = (subscriptionInfo: any, progress: GraduationProgress): GraduationReason | null => {
    const jobs = progress.jobCount ?? 0;
    if (hasSubscribed(subscriptionInfo)) return 'subscribed';
    if (progress.demoHeld && progress.allComplete) return 'all_steps_and_demo_held';
    if (progress.demoHeld && jobs >= GRADUATE_JOBS_AFTER_DEMO) return 'jobs_after_demo';
    if (jobs >= GRADUATE_JOBS_WITHOUT_DEMO) return 'jobs_without_demo';
    return null;
};

export const hasGraduated = (subscriptionInfo: any, progress: GraduationProgress): boolean =>
    getGraduationReason(subscriptionInfo, progress) !== null;

/** Eligible companies keep the onboarding dashboard until they graduate (see hasGraduated). */
export const shouldShowOnboarding = (subscriptionInfo: any, progress: GraduationProgress): boolean =>
    isOnboardingEligible(subscriptionInfo) && !hasGraduated(subscriptionInfo, progress);

export type DemoTiming =
    | { kind: 'none' }
    | { kind: 'unknownDate' }
    | { kind: 'past' }
    | { kind: 'upcoming'; days: number };

export const getDemoTiming = (booking: { startISO: string | null } | undefined, now: Date = new Date()): DemoTiming => {
    if (!booking) return { kind: 'none' };
    if (!booking.startISO) return { kind: 'unknownDate' };
    const start = moment(booking.startISO, moment.ISO_8601);
    if (!start.isValid()) return { kind: 'unknownDate' };
    const current = moment(now);
    if (start.isBefore(current)) return { kind: 'past' };
    return { kind: 'upcoming', days: start.clone().startOf('day').diff(current.clone().startOf('day'), 'days') };
};

export const formatDemoRelative = (days: number): string =>
    days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;

/** When a demo starts, in the user's own time zone, e.g. "22nd of October at 10:00am"; null without a usable date. */
export const formatDemoDate = (startISO: string | null | undefined): string | null => {
    if (!startISO) return null;
    const start = moment(startISO, moment.ISO_8601);
    return start.isValid() ? start.format('Do [of] MMMM [at] h:mma') : null;
};

/** A demo is still ahead of the user (a booking without a known date counts too). */
export const isDemoBooked = (timing: DemoTiming): boolean => timing.kind === 'upcoming' || timing.kind === 'unknownDate';

export type ManagerCardState = 'noDemo' | 'demoBooked' | 'managerAssigned';

/** `accountManagerAssigned`: GET /AccountManager/Get named someone (see accountManagerFromResponse). */
export const getManagerCardState = (timing: DemoTiming, demoHeld = false, accountManagerAssigned = false): ManagerCardState => {
    if (accountManagerAssigned) return 'managerAssigned';
    // After the demo the card waits on the account manager rather than asking for another booking.
    if (isDemoBooked(timing) || demoHeld) return 'demoBooked';
    return 'noDemo';
};

// HubSpot outcomes that mean the meeting didn't (or won't) happen. A reschedule leaves the old
// meeting RESCHEDULED and creates a new one, so it drops out here too.
const NOT_HAPPENING_OUTCOMES = ['CANCELED', 'CANCELLED', 'NO_SHOW', 'RESCHEDULED'];

export interface DemoFromMeetings {
    /** The next demo still to come (the earliest upcoming meeting), if any. */
    booking: { startISO: string | null; bookedAtISO: string } | undefined;
    /** A meeting has already taken place (marked completed, or its start has passed without being called off). */
    held: boolean;
    /** Who runs the next demo, or ran the last one. */
    owner: { name: string; email: string | null } | null;
}

const meetingStart = (m: ManagerMeeting) => {
    const raw = m.StartDate ?? m.MeetingDate;
    const start = raw ? moment(raw, moment.ISO_8601) : null;
    return start?.isValid() ? start : null;
};

/**
 * Reads the demo out of the company's HubSpot meetings (GET /Meeting/List). Every meeting on the
 * company's deal counts: HubSpot doesn't reliably tell a demo apart from other sales meetings.
 */
export const getDemoFromMeetings = (meetings: ManagerMeeting[] | null | undefined, now: Date = new Date()): DemoFromMeetings => {
    const current = moment(now);
    const live = (meetings ?? [])
        .filter(m => !m.Archived && !NOT_HAPPENING_OUTCOMES.includes((m.Outcome ?? '').trim().toUpperCase()))
        .map(m => ({ meeting: m, start: meetingStart(m) }));

    const isDone = (x: typeof live[number]) =>
        (x.meeting.Outcome ?? '').trim().toUpperCase() === 'COMPLETED' || (!!x.start && x.start.isBefore(current));
    const upcoming = live
        .filter(x => !isDone(x))
        .sort((a, b) => (a.start?.valueOf() ?? Infinity) - (b.start?.valueOf() ?? Infinity))[0];
    const lastHeld = live
        .filter(isDone)
        .sort((a, b) => (b.start?.valueOf() ?? 0) - (a.start?.valueOf() ?? 0))[0];

    const ownerOf = (m: ManagerMeeting | undefined) => {
        const name = m?.OwnerEmployeeFullName?.trim();
        return name ? { name, email: m?.OwnerEmployeeEmailAddress?.trim() || null } : null;
    };

    return {
        booking: upcoming
            ? { startISO: upcoming.start?.toISOString() ?? null, bookedAtISO: upcoming.meeting.CreatedAt }
            : undefined,
        held: !!lastHeld,
        owner: ownerOf(upcoming?.meeting) ?? ownerOf(lastHeld?.meeting),
    };
};

/**
 * The longest a booking made in the dashboard's iframe is shown on its own. HubSpot's copy replaces
 * it as soon as it arrives (see resolveDemoBooking). Manager waits an hour after each HubSpot sync
 * finishes before the next, so the extra 30 minutes covers a sync that lands a little late.
 */
export const LOCAL_BOOKING_TRUST_MINUTES = 90;

export const isLocalBookingFresh = (booking: { bookedAtISO: string } | undefined, now: Date = new Date()): boolean => {
    if (!booking) return false;
    const bookedAt = moment(booking.bookedAtISO, moment.ISO_8601);
    return bookedAt.isValid() && moment(now).diff(bookedAt, 'minutes', true) < LOCAL_BOOKING_TRUST_MINUTES;
};

// Slack between the browser's clock and HubSpot's CreatedAt when matching a local booking.
const CLOCK_SKEW_MINUTES = 10;

/**
 * The booking to show. HubSpot's upcoming demo when there is one. Otherwise a fresh iframe booking,
 * but only until HubSpot has caught up: once any meeting created around or after it comes through
 * (whatever its outcome), HubSpot's view wins, so a cancellation shows on the next sync.
 */
export const resolveDemoBooking = (
    meetings: ManagerMeeting[] | null | undefined,
    demo: DemoFromMeetings,
    local: { startISO: string | null; bookedAtISO: string } | undefined,
    now: Date = new Date(),
) => {
    if (demo.booking) return demo.booking;
    if (!local || !isLocalBookingFresh(local, now)) return undefined;
    const since = moment(local.bookedAtISO, moment.ISO_8601).subtract(CLOCK_SKEW_MINUTES, 'minutes');
    const synced = (meetings ?? []).some(m => {
        const createdAt = moment(m.CreatedAt, moment.ISO_8601);
        return createdAt.isValid() && !createdAt.isBefore(since);
    });
    return synced ? undefined : local;
};

/** A staff member by first name, with their photo when we have one (the APIs have no photos). */
export const toManager = (fullName: string, email: string | null = null): OnboardingManager => {
    const firstName = fullName.trim().split(/\s+/)[0];
    return {
        name: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        photoUrl: MANAGER_PHOTOS[firstName.toLowerCase()] ?? '',
        email,
    };
};

/** The manager to show for a meeting owner, or the fallback when there isn't one. */
export const managerFromOwner = (owner: { name: string } | null, fallback: OnboardingManager): OnboardingManager => {
    const name = owner?.name?.trim();
    return name ? toManager(name) : fallback;
};

const cleanString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * The tenant's account manager from a GET /AccountManager/Get response. Null when none is assigned
 * (`Result` null, which is also how an unreachable Manager answers), and for anything unusable:
 * an HTTP error body (utils/Fetch resolves those), a missing or blank FullName.
 */
export const accountManagerFromResponse = (response: unknown): OnboardingManager | null => {
    const result = (response as { Result?: Partial<AccountManager> | null } | null | undefined)?.Result;
    if (!result || typeof result !== 'object') return null;
    const fullName = cleanString(result.FullName);
    if (!fullName) return null;
    return toManager(fullName, cleanString(result.EmailAddress) || null);
};
